import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  UserPlus,
  Heart,
  ArrowLeft,
  PlayCircle,
  UserCheck,
} from "lucide-react";

import { creators } from "../../../../data/_creatorData"; // ✨ 2. Import from our new data file

import { mockCreator } from "../../../../data/mockData";
import Button from "../../../../components/ui/Button";
import PostCard from "../../../../components/shared/PostCard";
import CheckoutModal from "../../../../components/shared/CheckoutModal";

import SubscriptionTierCard from "./components/creatorPreview/SubscriptionTierCard";
import MerchCard from "./components/creatorPreview/MerchCard";
import EventCard from "./components/creatorPreview/EventCard";
import DonationCampaignCard from "./components/creatorPreview/DonationCampaignCard";
import ExperienceCard from "./components/creatorPreview/ExperienceCard";

const CourseCard = ({ course }) => {
  const [isCheckoutOpen, setCheckoutOpen] = useState(false);
  const courseItem = { ...course, name: course.title, type: "Course" }; // Adapt for modal

  return (
    <>
      <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center space-x-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
          <PlayCircle size={28} />
        </div>
        <div className="flex-grow">
          <p className="font-bold text-slate-800">{course.title}</p>
        </div>
        <Button onClick={() => setCheckoutOpen(true)} size="sm">
          Enroll Now
        </Button>
      </div>
      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setCheckoutOpen(false)}
        item={courseItem}
      />
    </>
  );
};

const CreatorProfilePage = () => {
  const [activeTab, setActiveTab] = useState("home");
  const [isFollowing, setIsFollowing] = useState(false);
  const [isTipModalOpen, setTipModalOpen] = useState(false); // ✨ 3. Add state for Tip Modal

  const { username } = useParams();
  const creator = creators.find((c) => c.username === username);

  // const creator = mockCreator;
  const navigate = useNavigate();

  if (!creator) {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-background">
        <p className="font-bold text-lg">Creator not found.</p>
        <Button onClick={() => navigate("/fan")} className="mt-4">
          Back to Home
        </Button>
      </div>
    );
  }

  // ✨ Define the "Tip" item for the checkout modal
  const tipItem = {
    name: `Tip for ${creator.fullName}`,
    priceETB: 50, // Default tip amount
    type: "Tip",
  };

  const tabs = [
    { id: "home", label: "Home" },
    { id: "subscribe", label: "Subscribe" },
    { id: "store", label: "Store" },
    { id: "experiences", label: "Experiences" },
    { id: "masterclass", label: "MasterClass" },
    { id: "events", label: "Events" },
    { id: "donations", label: "Donations" },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "home":
        return (
          <div className="p-4 space-y-4">
            {creator.posts.map((post) => (
              <PostCard key={post.id} post={post} author={creator} />
            ))}
          </div>
        );
      case "subscribe":
        return (
          <div className="p-4 space-y-4">
            {creator.subscriptionProducts.map((tier) => (
              <SubscriptionTierCard key={tier.id} tier={tier} />
            ))}
          </div>
        );
      case "store":
        return (
          <div className="p-4">
            <h3 className="text-lg font-bold text-slate-800 mb-3">
              Featured Merch
            </h3>
            <div className="space-y-3">
              {creator.merchandise.map((item) => (
                <MerchCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        );
      case "masterclass":
        return (
          <div className="p-4 space-y-4">
            {creator.courses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        );
      case "events":
        return (
          <div className="p-4 space-y-4">
            {creator.events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        );
      case "donations":
        return (
          <div className="p-4 space-y-4">
            {creator.donationCampaigns.map((campaign) => (
              <DonationCampaignCard key={campaign.id} campaign={campaign} />
            ))}
          </div>
        );
      case "experiences":
        return (
          <div className="p-4 space-y-4">
            {creator.experiences.map((exp) => (
              <ExperienceCard key={exp.id} experience={exp} />
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex flex-col h-full bg-[#fafbff] relative overflow-hidden">
        {/* Futuristic Background Elements */}
        <div className="absolute top-0 left-0 w-full h-[500px] mesh-bg opacity-40 pointer-events-none"></div>
        <div className="absolute top-[300px] right-[-100px] w-[300px] h-[300px] bg-primary/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute top-[600px] left-[-100px] w-[300px] h-[300px] bg-indigo-600/5 rounded-full blur-[100px] pointer-events-none"></div>

        {/* Global Action Bar (Floating at top) */}
        <div className="sticky top-0 z-30 flex items-center p-3 bg-white/60 backdrop-blur-xl border-b border-white/20">
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 bg-slate-100/50 hover:bg-white text-slate-800 rounded-full transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-grow text-center flex flex-col items-center">
            <h2 className="font-bold text-slate-900 leading-none">
              {creator.fullName}
            </h2>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">Creator Profile</p>
          </div>
          <div className="w-10"></div> {/* Spacer for symmetry */}
        </div>

        <div className="flex-grow overflow-y-auto no-scrollbar relative z-10">
          <div className="relative">
            {/* Banner Section with Gradient Mask */}
            <div className="relative h-44 overflow-hidden">
                <img
                    src={creator.bannerUrl}
                    alt={`${creator.fullName} banner`}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#fafbff] via-transparent to-black/20"></div>
            </div>

            <div className="px-5">
              <div className="flex justify-between items-end -mt-12 mb-4">
                <div className="relative">
                    <img
                        src={creator.profilePictureUrl}
                        alt={creator.fullName}
                        className="relative w-28 h-28 rounded-full object-cover border-[6px] border-[#fafbff] shadow-xl"
                    />
                    <div className="absolute bottom-1 right-2 w-6 h-6 bg-green-500 border-4 border-[#fafbff] rounded-full shadow-lg"></div>
                </div>
                
                <div className="flex items-center gap-2 pb-1">
                   {/* Tip Button - Minimalist Glass */}
                   <button
                     onClick={() => setTipModalOpen(true)}
                     className="p-3 bg-white/40 backdrop-blur-md border border-slate-200/50 rounded-2xl text-slate-500 hover:text-primary transition-all active:scale-95 group shadow-sm"
                   >
                     <Heart className="w-5 h-5" /> 
                   </button>
                   {isFollowing ? (
                     <Button
                       variant="outline"
                       size="lg"
                       className="rounded-2xl border-slate-200 font-bold !px-6 text-[11px] uppercase tracking-wider"
                       onClick={() => setIsFollowing(false)}
                     >
                       Following
                     </Button>
                   ) : (
                     <Button
                       variant="primary"
                       size="lg"
                       className="rounded-2xl shadow-md font-bold !px-8 text-[11px] uppercase tracking-wider"
                       onClick={() => setIsFollowing(true)}
                     >
                       Follow
                     </Button>
                   )}
                 </div>
               </div>

               {/* Bio Section - Refined Typography */}
               <div className="space-y-1">
                 <div className="flex items-center gap-2">
                     <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                     {creator.fullName}
                     </h1>
                     <div className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px] font-black uppercase tracking-tighter">Verified</div>
                 </div>
                 <p className="text-xs font-bold text-primary uppercase tracking-widest">@{creator.username}</p>
                 <p className="mt-3 text-slate-600 leading-relaxed text-sm font-medium">{creator.bio}</p>
               </div>

               {/* SOCIAL STATS DASHBOARD - Integrated Row */}
               <div className="mt-6 flex items-center justify-between px-6 py-4 bg-white/40 backdrop-blur-md rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                  {[
                    { label: 'Fans', value: creator.stats?.followers || '0' },
                    { label: 'Likes', value: creator.stats?.likes || '0' },
                    { label: 'Vault', value: creator.stats?.posts || '0' }
                  ].map((stat, i) => (
                    <React.Fragment key={i}>
                      <div className="flex flex-col items-center">
                         <p className="text-base font-black text-slate-800 tracking-tighter">{stat.value}</p>
                         <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{stat.label}</p>
                      </div>
                      {i < 2 && <div className="h-8 w-px bg-slate-200/50 mx-2"></div>}
                    </React.Fragment>
                  ))}
               </div>
            </div>
          </div>

          {/* TAB NAVIGATION - Classic Sleek Style */}
          <div className="sticky top-[69px] z-20 mt-6 bg-[#fafbff]/90 backdrop-blur-md border-b border-slate-100">
            <nav className="flex overflow-x-auto no-scrollbar px-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-4 text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap border-b-2 ${
                    activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-slate-400 hover:text-slate-600"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="pb-24 pt-2">{renderContent()}</div>
        </div>
      </div>
      {/* ✨ 5. Render the Tip Modal */}
      <CheckoutModal
        isOpen={isTipModalOpen}
        onClose={() => setTipModalOpen(false)}
        item={tipItem}
      />
    </>
  );
};

export default CreatorProfilePage;
